import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Input } from '../atoms/Input';
import { PATHS } from '../commons/paths';
import { useAuth } from '../hooks/useAuth';
import { VisibilityButton } from '../atoms/VisibilityButton';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { StyledLoginFormContainer } from 'templates/StyledLoginFormContainer';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { TransparentFooter } from 'templates/TransparentFooter';
import LoadingModal from 'organisms/LoadingModal';
import { StyledFormError } from 'organisms/u001/StyledFormError';
import { useLoginFormSchema } from 'hooks/u001/useLoginSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoginSchemaType } from 'commons/login';
import { SubmitHandler, useForm } from 'react-hook-form';
import { NotificationReceiver } from 'domains/User';
import { AppContext } from 'templates/AppContextProvider';

export function Login() {
  // hooks
  const navigate = useNavigate();
  const auth = useAuth();
  const { isGlobal, currentLang } = useContext(AppContext);
  const { t: tCommons } = useTranslationWithLang('commons');
  const [searchParam] = useSearchParams();
  const LoginSchema = useLoginFormSchema();
  const location = useLocation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
  } = useForm<LoginSchemaType>({
    resolver: zodResolver(LoginSchema),
  });
  useEffect(() => {
    clearErrors();
  }, [currentLang, clearErrors]);
  // state
  const recipient = location.state?.recipient;
  const [errorMessage, setErrorMessage] = useState('');
  const [isShowPassword, setIsShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // function
  const onClickVisibilityIcon = useCallback(
    () => setIsShowPassword((isShowPassword) => !isShowPassword),
    [isShowPassword]
  );
  const onClickLogin: SubmitHandler<LoginSchemaType> = useCallback(
    async (form) => {
      setLoading(true);
      setErrorMessage('');
      const result = await auth.signIn(
        form.userId,
        form.password,
        currentLang,
        {
          redirect: searchParam.get('redirect') ?? undefined,
        },
        recipient
      );
      if (result.success) {
        setLoading(false);
        navigate(PATHS.CONFIRM_MFA_CODE, { state: { linkToken: linkToken } });
      } else {
        setLoading(false);
        setErrorMessage(
          result.message === 'InvalidDomain'
            ? isGlobal
              ? tCommons('TU000_072') // このアカウントは日本国内アカウントです。日本SpotUサイトでログインしてください
              : tCommons('TU000_073') // このアカウントは日本国外アカウントです。海外SpotUサイトでログインしてください
            : result.message === 'FailedToSendVerificationCode'
              ? tCommons('TU000_074') // 認証コードの送信に失敗しました
              : result.message === 'FailedToGetMfaInfo'
                ? tCommons('TU000_071')
                : result.message === 'MailIsEmpty'
                  ? tCommons('TU000_105') // メールアドレスが登録されていません
                  : result.message === 'NotAuthorizedException'
                    ? tCommons('TU000_023') // IDまたはパスワードが誤っています
                    : result.message === 'NetworkError'
                      ? tCommons('TU000_057') // ネットワークエラーが発生しました。しばらくしてから再度お試しください。
                      : result.message === 'TimeoutError'
                        ? tCommons('TU000_107') // タイムアウトが発生しました。しばらくしてから再度お試しください。
                        : tCommons('TU000_058') // エラーが発生しました。
        );
      }
    },
    [navigate, auth, tCommons, searchParam, recipient]
  );
  // value
  const inputType = useMemo(() => (isShowPassword ? 'text' : 'password'), [isShowPassword]);
  const linkToken = searchParam.get('linkToken');
  const buttonLabel = useMemo(
    () => (linkToken ? tCommons('TU000_092') : tCommons('TU000_026')), // LINE連携 : ログイン
    [linkToken]
  );
  const handleNormalLogin = () => {
    if (auth.signInUser?.opts?.redirect) {
      const queryParam = new URLSearchParams();
      queryParam.set('redirect', auth.signInUser.opts.redirect);
      navigate(PATHS.LOGIN + '?' + queryParam.toString(), {
        state: { recipient: undefined },
      });
      return;
    }
    navigate(PATHS.LOGIN, { state: { recipient: undefined } });
  };
  return (
    <>
      {loading && <LoadingModal />}
      <StyledLoginFormContainer>
        <form
          className="px-8 py-4 flex flex-col w-full justify-center"
          onSubmit={handleSubmit(onClickLogin)}
        >
          <Input
            type="text"
            className="mb-6 px-5 rounded-full"
            placeholder={tCommons('TU000_016')}
            aria-label="id-input"
            autoComplete="username"
            {...register('userId')}
          />
          <div className="inline-block relative">
            <Input
              type={inputType}
              className="mb-2 pr-10 px-5 rounded-full"
              placeholder={tCommons('TU000_025')}
              aria-label="password-input"
              autoComplete="current-password"
              {...register('password')}
            />
            <VisibilityButton
              className="absolute top-2.5 right-3 cursor-pointer"
              isShow={isShowPassword}
              onClick={onClickVisibilityIcon}
            />
          </div>
          <div className="flex flex-col items-center w-full">
            <Link to={PATHS.RESET_PASSWORD} className="text-white">
              <ArrowRightIcon style={{ marginLeft: '-16px' }} /> {tCommons('TU000_024')}
            </Link>
            <Link to={PATHS.FORGET_USER_ID} className="text-white">
              <ArrowRightIcon style={{ marginLeft: '-16px' }} /> {tCommons('TU000_061')}
            </Link>
            {recipient === NotificationReceiver.MailAddress && (
              <button
                type="button"
                onClick={handleNormalLogin}
                className="text-white"
                title={tCommons('TU000_106')}
              >
                <ArrowRightIcon style={{ marginLeft: '-16px' }} />
                {tCommons('TU000_106')}
              </button>
            )}
            <StyledFormError
              errorMessage={
                errors.userId?.message || errors.password?.message || errorMessage || ''
              }
            />
            {recipient === NotificationReceiver.MailAddress ? (
              <button
                type="submit"
                className="text-[#d02a3be9] font-bold my-6 w-[14rem] bg-white hover:bg-opacity-80 rounded py-4 rounded-full"
                title={tCommons('TU000_062')}
              >
                {tCommons('TU000_062')}
              </button>
            ) : (
              <button
                type="submit"
                className="text-[#d02a3be9] font-bold my-6 w-[10rem] bg-white hover:bg-opacity-80 rounded py-4 rounded-full"
                title={buttonLabel}
              >
                {buttonLabel}
              </button>
            )}
          </div>
        </form>
        <TransparentFooter textColor={'#FFF'} />
      </StyledLoginFormContainer>
    </>
  );
}
